import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Save } from 'lucide-react';
import { Button, useToast } from '../components/ui';
import { useAppSelector } from '../store/hooks';
import { useGetProfileQuery, useUpdateProfileMutation, useLogoutMutation } from '../store/customersApi';
import { useGetMyOrdersQuery } from '../store/ordersApi';

export default function MiCuenta() {
  const navigate = useNavigate();
  const toast = useToast();
  const cachedCustomer = useAppSelector((s) => s.customerAuth.customer);
  const { data: profile } = useGetProfileQuery(undefined, { skip: !!cachedCustomer });
  const current = cachedCustomer ?? profile;

  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const [logout] = useLogoutMutation();
  const { data: orders } = useGetMyOrdersQuery();

  const [form, setForm] = useState({
    name: '', phone: '', address: '', addressLocality: '', addressProvince: '', addressPostalCode: '',
  });

  useEffect(() => {
    if (current) {
      setForm({
        name: current.name,
        phone: current.phone,
        address: current.address,
        addressLocality: current.addressLocality,
        addressProvince: current.addressProvince,
        addressPostalCode: current.addressPostalCode,
      });
    }
  }, [current]);

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProfile(form).unwrap();
      toast.success({ title: 'Perfil actualizado' });
    } catch (e: any) {
      toast.error({ title: 'No se pudo actualizar', description: e?.data?.message ?? 'Intentá de nuevo.' });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const inputClass = 'w-full bg-slate-50 border border-slate-200 focus:border-indigo-500/50 focus:bg-white rounded-xl px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none transition-colors';

  if (!current) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 pt-[81px] pb-16">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading font-extrabold text-2xl text-slate-900">Mi cuenta</h1>
        <Button variant="outline" size="sm" leftIcon={<LogOut className="w-3.5 h-3.5" />} onClick={handleLogout}>
          Cerrar sesión
        </Button>
      </div>

      <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 space-y-4">
        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Email</p>
        <p className="text-sm text-slate-700 -mt-2">{current.email}</p>

        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Nombre completo</label>
        <input value={form.name} onChange={set('name')} className={inputClass} />

        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Teléfono</label>
        <input value={form.phone} onChange={set('phone')} className={inputClass} />

        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Dirección</label>
        <input value={form.address} onChange={set('address')} className={inputClass} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Localidad</label>
            <input value={form.addressLocality} onChange={set('addressLocality')} className={inputClass} />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Provincia</label>
            <input value={form.addressProvince} onChange={set('addressProvince')} className={inputClass} />
          </div>
        </div>

        <label className="block text-[10px] text-slate-500 font-bold uppercase tracking-wider">Código postal</label>
        <input value={form.addressPostalCode} onChange={set('addressPostalCode')} className={inputClass} />

        <Button type="submit" variant="primary" loading={isSaving} leftIcon={<Save className="w-3.5 h-3.5" />} className="mt-2">
          Guardar cambios
        </Button>
      </form>

      <div className="mt-10">
        <h2 className="font-heading font-extrabold text-xl text-slate-900 mb-4">Mis pedidos</h2>
        {!orders || orders.length === 0 ? (
          <p className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl p-6 text-center">
            Todavía no hiciste ningún pedido.
          </p>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Pedido #{o.id.slice(0, 8)}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(o.createdAt * 1000).toLocaleDateString('es-AR')} · {o.deliveryType === 'envio' ? 'Envío' : 'Retiro'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">
                    ${o.total.toLocaleString('es-AR', { maximumFractionDigits: 0 })}
                  </p>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md border bg-slate-50 border-slate-200 text-slate-600">
                    {o.paymentStatus === 'paid' ? 'Pagado' : o.paymentStatus === 'pending' ? 'Pendiente' : o.paymentStatus === 'rejected' ? 'Rechazado' : o.paymentStatus === 'review' ? 'En revisión' : 'Cancelado'}
                    {' · '}
                    {o.fulfillmentStatus === 'preparando' ? 'Preparando' : o.fulfillmentStatus === 'enviado' ? 'Enviado' : 'Entregado'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
